import { useState, useEffect, useCallback } from "react";
import axios from "axios";

const INITIAL_FORM_STATE = {
  code: "",
  name: "",
  description: "",
  isGroupLevel: false,
  isActive: true,
  isSystemRole: false,
  isDangerous: false,
  isLocked: false,
};

const CHAR_LIMITS = {
  code: 25,
  name: 50,
  description: 255,
};

// ── Helpers ──────────────────────────────────────────────────────────────────

function sanitizeCode(raw) {
  return raw
    .toUpperCase()
    .replace(/ /g, "_")
    .replace(/[^A-Z0-9_]/g, "")
    .slice(0, CHAR_LIMITS.code);
}

function validate(form) {
  const errors = {};
  if (!form.code) errors.code = "Code is required.";
  if (!form.name) {
    errors.name = "Name is required.";
  } else if (form.name.trim().length < 4) {
    errors.name = "Name must be at least 4 characters.";
  }
  return errors;
}

// ── Sub-components ────────────────────────────────────────────────────────────

function FieldError({ message }) {
  if (!message) return null;
  return (
    <p role="alert" className="mt-1 text-xs text-red-600 font-medium">
      {message}
    </p>
  );
}

function CharCount({ value, max }) {
  const remaining = max - value.length;
  const nearLimit = remaining <= Math.floor(max * 0.15);
  return (
    <span
      className={`text-xs tabular-nums ${
        nearLimit ? "text-amber-600 font-semibold" : "text-slate-400"
      }`}
    >
      {remaining}/{max}
    </span>
  );
}

function TextInput({ id, label, value, onChange, error, maxLength, hint, required, ...rest }) {
  return (
    <div>
      <div className="flex items-baseline justify-between mb-1">
        <label htmlFor={id} className="block text-sm font-medium text-slate-700">
          {label}
          {required && <span className="ml-0.5 text-red-500">*</span>}
        </label>
        <CharCount value={value} max={maxLength} />
      </div>
      {hint && <p className="mb-1 text-xs text-slate-500">{hint}</p>}
      <input
        id={id}
        type="text"
        value={value}
        onChange={onChange}
        maxLength={maxLength}
        aria-invalid={!!error}
        aria-describedby={error ? `${id}-error` : undefined}
        className={`w-full rounded-md border px-3 py-2 text-sm text-slate-800 placeholder:text-slate-400
          bg-white shadow-sm transition-colors
          focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500
          ${error ? "border-red-400 bg-red-50" : "border-slate-300 hover:border-slate-400"}`}
        {...rest}
      />
      {error && <FieldError message={error} id={`${id}-error`} />}
    </div>
  );
}

function TextAreaInput({ id, label, value, onChange, error, maxLength }) {
  return (
    <div>
      <div className="flex items-baseline justify-between mb-1">
        <label htmlFor={id} className="block text-sm font-medium text-slate-700">
          {label}
        </label>
        <CharCount value={value} max={maxLength} />
      </div>
      <textarea
        id={id}
        value={value}
        onChange={onChange}
        maxLength={maxLength}
        rows={3}
        aria-invalid={!!error}
        placeholder="Optional description…"
        className={`w-full rounded-md border px-3 py-2 text-sm text-slate-800 placeholder:text-slate-400
          bg-white shadow-sm resize-none transition-colors
          focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500
          ${error ? "border-red-400 bg-red-50" : "border-slate-300 hover:border-slate-400"}`}
      />
      {error && <FieldError message={error} />}
    </div>
  );
}

function CheckboxField({ id, label, description, checked, onChange, danger }) {
  return (
    <label
      htmlFor={id}
      className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors select-none
        ${
          danger
            ? "border-red-200 bg-red-50 hover:bg-red-100"
            : "border-slate-200 bg-slate-50 hover:bg-slate-100"
        }`}
    >
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className={`mt-0.5 h-4 w-4 rounded border-slate-300 transition-colors flex-shrink-0
          ${danger ? "text-red-600 focus:ring-red-500" : "text-indigo-600 focus:ring-indigo-500"}`}
      />
      <div>
        <span className={`block text-sm font-medium ${danger ? "text-red-700" : "text-slate-700"}`}>
          {label}
        </span>
        {description && (
          <span className="block text-xs text-slate-500 mt-0.5">{description}</span>
        )}
      </div>
    </label>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function AddRoleModal({ isOpen, onClose, onSuccess }) {
  const [form, setForm] = useState(INITIAL_FORM_STATE);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState(null);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setForm(INITIAL_FORM_STATE);
      setErrors({});
      setApiError(null);
    }
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e) => {
      if (e.key === "Escape" && !isSubmitting) onClose();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [isOpen, isSubmitting, onClose]);

  const handleTextField = useCallback((field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  }, []);

  const handleCodeChange = useCallback((e) => {
    setForm((prev) => ({ ...prev, code: sanitizeCode(e.target.value) }));
    setErrors((prev) => ({ ...prev, code: undefined }));
  }, []);

  const handleCheckbox = useCallback((field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.checked }));
  }, []);

  const handleSubmit = async () => {
    const validationErrors = validate(form);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    setApiError(null);

    try {
      const API_BASE = window.MANTLE_CONFIG.API_URL ?? 'http://localhost:5000/api';
      const body = {
          code: form.code,
          name: form.name.trim(),
          description: form.description.trim(),
          is_group_level: form.isGroupLevel,
          is_active: form.isActive,
          is_system_role: form.isSystemRole,
          is_dangerous: form.isDangerous,
          is_locked: form.isLocked,
          created_by: localStorage.getItem ('uid')
        };
        const token = localStorage.getItem ('jwt');
    
      const data = await axios.post(`${API_BASE}/users/add-role`, body, {
        headers: {
          'Authorization': 'Bearer ' + token,
          'x-db-name': window.MANTLE_CONFIG.TENANT_DB 
        },
      });
      
      onSuccess(data?.data ?? data); onClose(); 
    } catch {
      setApiError("Network error; please check your connection and try again!");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    /* Backdrop */
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
    >
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
        onClick={!isSubmitting ? onClose : undefined}
        aria-hidden="true"
      />

      {/* Panel */}
      <div className="relative z-10 w-full max-w-lg bg-white rounded-xl shadow-2xl flex flex-col max-h-[90vh]">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <h2 id="modal-title" className="text-base font-semibold text-slate-900">
              Add New Role
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Fields marked <span className="text-red-500 font-medium">*</span> are required.
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            aria-label="Close dialog"
            className="rounded-md p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors disabled:opacity-40"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body (scrollable) */}
        <div className="overflow-y-auto px-6 py-5 flex flex-col gap-5">

          {/* API-level error banner */}
          {apiError && (
            <div
              role="alert"
              className="flex items-start gap-2.5 rounded-lg border border-red-200 bg-red-50 px-4 py-3"
            >
              <svg className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm-.75-9.25a.75.75 0 011.5 0v3.5a.75.75 0 01-1.5 0v-3.5zm.75 6.5a.875.875 0 110-1.75.875.875 0 010 1.75z" clipRule="evenodd" />
              </svg>
              <p className="text-sm text-red-700">{apiError}</p>
            </div>
          )}

          {/* Code */}
          <TextInput
            id="role-code"
            label="Code"
            value={form.code}
            onChange={handleCodeChange}
            error={errors.code}
            maxLength={CHAR_LIMITS.code}
            hint="Auto-formatted: uppercase, spaces become underscores."
            required
            placeholder="E.G. ADMIN_USER"
          />

          {/* Name */}
          <TextInput
            id="role-name"
            label="Name"
            value={form.name}
            onChange={handleTextField("name")}
            error={errors.name}
            maxLength={CHAR_LIMITS.name}
            required
            placeholder="e.g. Admin User"
          />

          {/* Description */}
          <TextAreaInput
            id="role-description"
            label="Description"
            value={form.description}
            onChange={handleTextField("description")}
            error={errors.description}
            maxLength={CHAR_LIMITS.description}
          />

          {/* Flags */}
          <div>
            <p className="text-sm font-medium text-slate-700 mb-2">Role Flags</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <CheckboxField
                id="flag-group-level"
                label="Group Level?"
                description="Applies permissions at the group scope."
                checked={form.isGroupLevel}
                onChange={handleCheckbox("isGroupLevel")}
              />
              <CheckboxField
                id="flag-active"
                label="Active?"
                description="Role is enabled and assignable."
                checked={form.isActive}
                onChange={handleCheckbox("isActive")}
              />
              <CheckboxField
                id="flag-system-role"
                label="System Role?"
                description="Managed by the system; use with care."
                checked={form.isSystemRole}
                onChange={handleCheckbox("isSystemRole")}
              />
              <CheckboxField
                id="flag-locked"
                label="Locked?"
                description="Prevents editing after creation."
                checked={form.isLocked}
                onChange={handleCheckbox("isLocked")}
              />
              <CheckboxField
                id="flag-dangerous"
                label="Dangerous?"
                description="Grants elevated or destructive permissions."
                checked={form.isDangerous}
                onChange={handleCheckbox("isDangerous")}
                danger
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 bg-slate-50 rounded-b-xl">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg
              hover:bg-slate-50 hover:border-slate-400 transition-colors disabled:opacity-40"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="inline-flex items-center gap-2 px-5 py-2 text-sm font-medium text-white
              bg-blue-600 rounded-lg hover:bg-blue-700 active:bg-blue-800
              transition-colors disabled:opacity-60 disabled:cursor-not-allowed shadow-sm"
          >
            {isSubmitting && (
              <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
              </svg>
            )}
            {isSubmitting ? "Saving …" : "Save Role"}
          </button>
        </div>
      </div>
    </div>
  );
}
